import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Stack,
  Paper,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
  Zoom
} from '@mui/material';
import {
  LocationOnRounded,
  PhoneRounded,
  EmailRounded,
  AccessTimeRounded,
  SendRounded,
  SupportAgentRounded,
  ChatRounded,
  HeadsetMicRounded,
  BusinessRounded,
  PersonRounded,
  LanguageRounded,
  SecurityRounded,
  SpeedRounded,
  CheckCircleRounded,
  ExpandMoreRounded,
  WhatsApp,
  Telegram,
  LinkedIn,
  Twitter,
  Instagram,
  YouTube,
  RocketLaunchRounded,
  AutoAwesomeRounded
} from '@mui/icons-material';

const ContactPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [expandedFaq, setExpandedFaq] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const heroRef = useRef(null);
  const [isHeroVisible, setIsHeroVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsHeroVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );
    if (heroRef.current) observer.observe(heroRef.current);
    return () => observer.disconnect();
  }, []);

  const contactInfo = [
    {
      icon: <LocationOnRounded />,
      title: 'Adres',
      info: 'Maslak Mahallesi, Büyükdere Cd. No:255\nSarıyer/İstanbul 34398',
      color: '#3b82f6'
    },
    {
      icon: <PhoneRounded />,
      title: 'Telefon',
      info: '+90 212 555 0123\n+90 532 555 0123',
      color: '#10b981'
    },
    {
      icon: <EmailRounded />,
      title: 'E-posta',
      info: 'info@habersiteniz.com\nsupport@habersiteniz.com',
      color: '#f59e0b'
    },
    {
      icon: <AccessTimeRounded />,
      title: 'Çalışma Saatleri',
      info: 'Pazartesi - Cuma: 09:00 - 18:00\nCumartesi: 10:00 - 16:00',
      color: '#ef4444'
    }
  ];

  const supportChannels = [
    {
      icon: <SupportAgentRounded />,
      title: 'Canlı Destek',
      description: '7/24 canlı destek hizmeti',
      action: 'Sohbet Başlat',
      color: '#3b82f6'
    },
    {
      icon: <WhatsApp />,
      title: 'WhatsApp',
      description: 'Hızlı mesajlaşma',
      action: 'Mesaj Gönder',
      color: '#25d366'
    },
    {
      icon: <Telegram />,
      title: 'Telegram',
      description: 'Teknik destek kanalı',
      action: 'Kanala Katıl',
      color: '#0088cc'
    },
    {
      icon: <HeadsetMicRounded />,
      title: 'Telefon Desteği',
      description: 'Sesli görüşme',
      action: 'Hemen Ara',
      color: '#10b981'
    }
  ];

  const faqData = [
    {
      question: 'Kurulum süreci ne kadar sürer?',
      answer: 'Standart kurulum 24-48 saat içinde tamamlanır. Özel geliştirmeler için süre proje kapsamına göre belirlenir. Kurulum sürecinde size adım adım rehberlik ederiz.'
    },
    {
      question: 'Teknik destek nasıl alırım?',
      answer: 'Canlı chat, WhatsApp, Telegram ve telefon ile 7/24 teknik destek alabilirsiniz. Ayrıca detaylı dokümantasyon ve video eğitimlerimiz mevcuttur.'
    },
    {
      question: 'Veri güvenliği nasıl sağlanıyor?',
      answer: 'SSL şifreleme, günlük yedekleme, ISO 27001 sertifikalı veri merkezleri ve KVKK uyumluluğu ile verileriniz tamamen güvende tutulur.'
    },
    {
      question: 'Özel geliştirme yapılıyor mu?',
      answer: 'Evet, ihtiyaçlarınıza özel modül ve entegrasyon geliştirmeleri yapıyoruz. Proje detaylarını görüştükten sonra teklif sunuyoruz.'
    },
    {
      question: 'Eğitim ve dokümantasyon var mı?',
      answer: 'Kapsamlı video eğitimler, adım adım kılavuzlar ve canlı eğitim seansları sunuyoruz. Ayrıca kullanım dokümantasyonumuz sürekli güncellenir.'
    },
    {
      question: 'Paket değişikliği nasıl yapılır?',
      answer: 'Paket değişikliği istediğiniz zaman yapılabilir. Üst pakete geçiş anında, alt pakete geçiş sonraki fatura döneminde geçerli olur.'
    }
  ];

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Form submission logic here
    setShowSuccess(true);
    // Reset form
    setFormData({
      name: '',
      email: '',
      company: '',
      phone: '',
      subject: '',
      message: ''
    });
  };

  const handleFaqExpand = (panel) => (event, isExpanded) => {
    setExpandedFaq(isExpanded ? panel : false);
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        ref={heroRef}
        sx={{
          minHeight: '56vh',
          background: `
            linear-gradient(135deg, #0f0f23 0%, #1a1a2e 25%, #16213e 50%, #0f3460 75%, #0e4b99 100%),
            radial-gradient(circle at 20% 30%, rgba(59, 130, 246, 0.3) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgba(16, 185, 129, 0.3) 0%, transparent 50%)
          `,
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              repeating-linear-gradient(
                90deg,
                transparent,
                transparent 98px,
                rgba(255, 255, 255, 0.03) 100px
              )
            `,
            zIndex: 1
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid containerxs={12} md={8} mx="auto" textAlign="center">
            <Grid item xs={12}>
              <Fade in={isHeroVisible} timeout={1000}>
                <Box>
                  <Chip
                    label="📞 İLETİŞİM"
                    sx={{
                      mb: 3,
                      background: 'linear-gradient(45deg, #3b82f6, #10b981)',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      px: 2,
                      py: 0.5,
                      animation: 'pulse 2s infinite'
                    }}
                  />
                  
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: '2.5rem', md: '4rem' },
                      fontWeight: 800,
                      background: 'linear-gradient(45deg, #ffffff, #a78bfa, #60a5fa)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 3,
                      lineHeight: 1.2
                    }}
                  >
                    Bizimle
                    <br />
                    <span style={{ 
                      background: 'linear-gradient(45deg, #10b981, #06b6d4)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}>
                      İletişime Geçin
                    </span>
                  </Typography>

                  <Typography
                    variant="h5"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      mb: 4,
                      fontWeight: 300,
                      lineHeight: 1.6
                    }}
                  >
                    Size özel çözümler için uzman ekibimizle iletişime geçin. 
                    7/24 destek ile yanınızdayız!
                  </Typography>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" alignItems="center">
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<ChatRounded />}
                      sx={{
                        background: 'linear-gradient(45deg, #3b82f6, #1d4ed8)',
                        color: 'white',
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        borderRadius: '50px',
                        boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 12px 40px rgba(59, 130, 246, 0.4)'
                        }
                      }}
                      onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}
                    >
                      Mesaj Gönder
                    </Button>
                    
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<PhoneRounded />}
                      sx={{
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                        color: 'white',
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        borderRadius: '50px',
                        '&:hover': {
                          borderColor: 'white',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        }
                      }}
                    >
                      Hemen Ara
                    </Button>
                  </Stack>
                </Box>
              </Fade>
            </Grid>

           
          </Grid>
        </Container>

        <style jsx>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
            50% { opacity: 0.8; transform: translate(-50%, -50%) scale(1.05); }
          }
          
          @keyframes orbit {
            from { transform: translate(-50%, -50%) rotate(0deg) translateX(120px) rotate(0deg); }
            to { transform: translate(-50%, -50%) rotate(360deg) translateX(120px) rotate(-360deg); }
          }
        `}</style>
      </Box>


         {/* Contact Form */}
         <Box 
        id="contact-form"
        sx={{ py: 8, background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <Box>
                <Chip
                  label="✉️ MESAJ GÖNDERİN"
                  sx={{
                    mb: 3,
                    background: 'linear-gradient(45deg, #f59e0b, #ef4444)',
                    color: 'white',
                    fontWeight: 600,
                    px: 2
                  }}
                />
                <Typography variant="h3" fontWeight={700} color="#1e293b" mb={3}>
                  Size Nasıl Yardımcı Olabiliriz?
                </Typography>
                <Typography variant="h6" color="#64748b" mb={4} lineHeight={1.6}>
                  Projeleriniz hakkında detaylı bilgi alın. Uzman ekibimiz 
                  size özel çözümler sunmak için hazır.
                </Typography>

                <List>
                  {[
                    'Ücretsiz danışmanlık',
                    '24 saat içinde geri dönüş',
                    'Kişiye özel çözümler',
                    'Teknik destek garantisi'
                  ].map((item, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon>
                        <CheckCircleRounded sx={{ color: '#10b981' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={item}
                        primaryTypographyProps={{
                          fontWeight: 500,
                          color: '#475569'
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 4,
                  borderRadius: 4,
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.08)',
                  border: '1px solid rgba(0, 0, 0, 0.05)'
                }}
              >
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Ad Soyad"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="E-posta"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Şirket"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Telefon"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Konu"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Mesajınız"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        multiline
                        rows={4}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        endIcon={<SendRounded />}
                        sx={{
                          background: 'linear-gradient(45deg, #3b82f6, #1d4ed8)',
                          color: 'white',
                          py: 2,
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          borderRadius: 3,
                          boxShadow: '0 8px 25px rgba(59, 130, 246, 0.3)',
                          '&:hover': {
                            background: 'linear-gradient(45deg, #2563eb, #1e40af)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 12px 35px rgba(59, 130, 246, 0.4)'
                          }
                        }}
                      >
                        Mesajı Gönder
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      

      {/* Contact Info Cards */}
      <Box sx={{ py: 8, background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Chip
              label="📍 İLETİŞİM BİLGİLERİ"
              sx={{
                mb: 3,
                background: 'linear-gradient(45deg, #4f46e5, #06b6d4)',
                color: 'white',
                fontWeight: 600,
                px: 2
              }}
            />
            <Typography variant="h3" fontWeight={700} color="#1e293b" mb={2}>
              Bize Ulaşmanın Yolları
            </Typography>
            <Typography variant="h6" color="#64748b" maxWidth="600px" mx="auto">
              Size en uygun iletişim kanalını seçin ve hemen destek alın
            </Typography>
          </Box>

          <Grid container spacing={4} mb={8}>
            {contactInfo.map((info, index) => (
              <Grid item xs={12} md={6} lg={3} key={index}>
                <Zoom in timeout={300 + index * 100}>
                  <Card
                    sx={{
                      height: '100%',
                      textAlign: 'center',
                      borderRadius: 4,
                      border: '1px solid rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 20px 40px ${info.color}20`,
                        borderColor: info.color
                      }
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Avatar
                        sx={{
                          width: 64,
                          height: 64,
                          mx: 'auto',
                          mb: 2,
                          background: `linear-gradient(45deg, ${info.color}, ${info.color}dd)`,
                          boxShadow: `0 8px 25px ${info.color}30`
                        }}
                      >
                        {info.icon}
                      </Avatar>
                      <Typography variant="h6" fontWeight={600} color="#1e293b" mb={2}>
                        {info.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        color="#64748b"
                        sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}
                      >
                        {info.info}
                      </Typography>
                    </CardContent>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>

          {/* Support Channels */}
          <Grid container spacing={3}>
            {supportChannels.map((channel, index) => (
              <Grid item xs={12} md={6} lg={3} key={index}>
                <Fade in timeout={600 + index * 100}>
                  <Paper
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      borderRadius: 4,
                      border: `1px solid ${channel.color}20`,
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: `0 15px 35px ${channel.color}25`,
                        borderColor: channel.color
                      }
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        mx: 'auto',
                        mb: 2,
                        backgroundColor: channel.color,
                        color: 'white'
                      }}
                    >
                      {channel.icon}
                    </Avatar>
                    <Typography variant="h6" fontWeight={600} color="#1e293b" mb={1}>
                      {channel.title}
                    </Typography>
                    <Typography variant="body2" color="#64748b" mb={2}>
                      {channel.description}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{
                        borderColor: channel.color,
                        color: channel.color,
                        '&:hover': {
                          backgroundColor: `${channel.color}10`,
                          borderColor: channel.color
                        }
                      }}
                    >
                      {channel.action}
                    </Button>
                  </Paper>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

   

      {/* FAQ Section */}
      <Box sx={{ py: 8, background: 'linear-gradient(180deg, #ffffff 0%, #f1f5f9 100%)' }}>
        <Container maxWidth="md">
          <Box textAlign="center" mb={6}>
            <Chip
              label="❓ SIKÇA SORULAN SORULAR"
              sx={{
                mb: 3,
                background: 'linear-gradient(45deg, #7c3aed, #a855f7)',
                color: 'white',
                fontWeight: 600,
                px: 2
              }}
            />
            <Typography variant="h3" fontWeight={700} color="#1e293b" mb={2}>
              Hızlı Cevaplar
            </Typography>
            <Typography variant="h6" color="#64748b">
              En çok merak edilen konular hakkında bilgi alın
            </Typography>
          </Box>

          {faqData.map((faq, index) => (
            <Accordion
              key={index}
              expanded={expandedFaq === index}
              onChange={handleFaqExpand(index)}
              sx={{
                mb: 2,
                borderRadius: 3,
                border: '1px solid rgba(0, 0, 0, 0.1)',
                '&:before': { display: 'none' },
                boxShadow: expandedFaq === index ? '0 8px 25px rgba(0, 0, 0, 0.1)' : 'none',
                '&:hover': {
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)'
                }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreRounded />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center'
                  }
                }}
              >
                <Typography variant="h6" fontWeight={600} color="#1e293b">
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" color="#64748b" lineHeight={1.7}>
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* Map Section */}
      <Box sx={{ py: 8, background: '#f8fafc' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Chip
              label="🗺️ KONUMUMUZ"
              sx={{
                mb: 3,
                background: 'linear-gradient(45deg, #059669, #0891b2)',
                color: 'white',
                fontWeight: 600,
                px: 2
              }}
            />
            <Typography variant="h3" fontWeight={700} color="#1e293b" mb={2}>
              Bizi Ziyaret Edin
            </Typography>
            <Typography variant="h6" color="#64748b">
             Aydın'daki ofisimizde sizleri ağırlamaktan mutluluk duyarız
            </Typography>
          </Box>

          <Box sx={{ mt: 10, mb: 8 }}>
        <Container maxWidth="lg">
          <Box>
            <Card
              elevation={0}
              sx={{
                height: 450,
                borderRadius: '24px',
                overflow: 'hidden',
                boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
              }}
            >
              <Box
                component="iframe"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3150.9108246273577!2d27.84341167742642!3d37.838973471969354!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14b92b0005c6d277%3A0x3a3c693e1c9609fb!2sR%C3%96NESANS%20PLAZA!5e0!3m2!1sen!2str!4v1753445195911!5m2!1sen!2str"
                sx={{
                  border: 0,
                  width: '100%',
                  height: '100%',
                }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Rönesis EBYS Konum"
              />
            </Card>
          </Box>
        </Container>
      </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      {/* <Box
        sx={{
          py: 8,
          background: `
            linear-gradient(135deg, #0f0f23 0%, #1a1a2e 25%, #16213e 50%, #0f3460 75%, #0e4b99 100%),
            radial-gradient(circle at 30% 40%, rgba(59, 130, 246, 0.4) 0%, transparent 50%),
            radial-gradient(circle at 70% 60%, rgba(16, 185, 129, 0.3) 0%, transparent 50%)
          `,
          position: 'relative',
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h3"
            fontWeight={700}
            sx={{
              background: 'linear-gradient(45deg, #ffffff, #a78bfa)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 3
            }}
          >
            Hemen Başlayalım!
          </Typography>
          
          <Typography
            variant="h6"
            sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 4, lineHeight: 1.6 }}
          >
            Projelerinizi hayata geçirmek için uzman ekibimizle iletişime geçin. 
            Size özel çözümler sunmaya hazırız!
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              startIcon={<RocketLaunchRounded />}
              sx={{
                background: 'linear-gradient(45deg, #3b82f6, #1d4ed8)',
                color: 'white',
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                fontWeight: 600,
                borderRadius: '50px',
                boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 40px rgba(59, 130, 246, 0.4)'
                }
              }}
            >
              Projemi Başlat
            </Button>
            
            <Button
              variant="outlined"
              size="large"
              startIcon={<AutoAwesomeRounded />}
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.3)',
                color: 'white',
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                borderRadius: '50px',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              Demo İste
            </Button>
          </Stack>
        </Container>
      </Box> */}

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          sx={{
            width: '100%',
            borderRadius: 2,
            boxShadow: '0 8px 25px rgba(16, 185, 129, 0.3)'
          }}
        >
          Mesajınız başarıyla gönderildi! 24 saat içinde size dönüş yapacağız.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactPage;
